<template>
    <div class="mat-container">
        <h5 class="is-5 title mb-2">
            {{ mat.name }}
        </h5>

        <div v-if="fightsForMat && fightsForMat.length > 0">
            <div class="bottom-buffer hidden-print">
                <div class="buttons are-small mb-2">
                    <button class="button" @click="refreshFights(mat.id)">
                        <span class="icon">
                            <i class="fa fa-sync-alt" />
                        </span>
                        <span>{{ translate("common.refresh") }}</span>
                    </button>

                    <button class="button" @click="openFinishedFights">
                        {{ translate("admin_fights_manage.finished_fights") }}
                    </button>

                    <button v-if="!moveFightsMode" class="button" @click="openReorderModal">
                        <span class="icon">
                            <i class="fas fa-retweet"></i>
                        </span>
                        <span>{{ translate("admin_fights_manage.change_order") }}</span>
                    </button>

                    <button v-if="!moveFightsMode" class="button" @click="startMovingFights" :disabled="!!fightsStore.matIdToSelectFrom">
                        <span class="icon">
                            <i class="fa fa-arrows-alt" />
                        </span>
                        <span>{{ translate("admin_fights_manage.move_fights") }}</span>
                    </button>
                </div>

                <div v-if="moveFightsMode" class="message mb-2" :class="totalSelectedFights > 0 ? 'is-info' : 'is-warning'">
                    <div class="message-body p-3">
                        <div class="is-flex is-justify-content-space-between is-align-items-center">
                            <span>
                                <span v-if="totalSelectedFights > 0">
                                    {{
                                        translate("admin_fights_manage.selected_fights_to_move", false, [
                                            totalSelectedFights,
                                            totalTimeOfSelectedFights,
                                        ])
                                    }}
                                </span>
                                <span v-else>
                                    {{ translate("admin_fights_manage.select_fights_to_move") }}
                                </span>
                            </span>
                            <div class="buttons are-small">
                                <button class="button is-success" @click="confirmSelectedFights" :disabled="totalSelectedFights === 0">
                                    {{ translate("admin_fights_manage.confirm_move") }}
                                </button>
                                <button class="button is-danger" @click="cancelMovingFights">
                                    {{ translate("common.cancel") }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <table class="table is-hoverable is-fullwidth" @mouseleave="fightHover(0)">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{{ translate("common.category") }}</th>
                        <th>{{ translate("common.round") }}</th>
                        <th>{{ translate("page.start_fights.competitor") }} #1</th>
                        <th>{{ translate("page.start_fights.competitor") }} #2</th>
                        <th>{{ translate("page.start_fights.fight_time") }}</th>
                        <th></th>
                    </tr>
                </thead>

                <transition-group tag="tbody" name="fade">
                    <FightTableRow
                        :selection-mode="moveFightsMode"
                        :show-duplicated-pair="true"
                        :fight-queue-status="fightQueueStatus"
                        v-for="(fight, index) in fightsForMat"
                        :fight="fight"
                        :index="index"
                        :key="fight.id"
                        @selectCategory="onSelectedCategoryToMove"
                        @fightHover="fightHover"
                        :highlight-parameter-id="highlightParameterId"
                    >
                        <template v-slot:afterCells>
                            <td>
                                {{ fight.fightTime.time }}
                            </td>
                            <td>
                                <DropdownButton>
                                    <template v-slot:trigger>
                                        <button class="button is-small">
                                            <span>{{ translate("common.options") }}</span>
                                            <span class="icon">
                                                <i class="fa fa-caret-down"></i>
                                            </span>
                                        </button>
                                    </template>
                                    <template v-slot:content>
                                        <a class="dropdown-item" @click="openReshuffleCompetitorsModal(fight.id)">
                                            <span class="icon">
                                                <i class="fas fa-sync"></i>
                                            </span>
                                            <span>{{ translate("reshuffle_competitors_modal.title") }}</span>
                                        </a>

                                        <a class="dropdown-item" v-if="isFightQueueBreak(fight)" @click="removeQueueBreak(fight)">
                                            <span>{{ translate("admin_fights_manage.remove_break") }}</span>
                                        </a>
                                        <a class="dropdown-item" v-else @click="insertBreakBeforeFight(fight)">
                                            <span class="icon">
                                                <i class="fas fa-mug-hot"></i>
                                            </span>
                                            <span>{{ translate("admin_fights_manage.insert_break_before") }}</span>
                                        </a>
                                    </template>
                                </DropdownButton>
                            </td>
                        </template>
                    </FightTableRow>
                </transition-group>
            </table>
        </div>
        <!-- if fights table is empty but mat is selected (non null) -->
        <div v-else-if="fightsForMat && fightsForMat.length === 0">
            <div class="mb-2">
                <button class="button is-small" @click="openFinishedFights">
                    {{ translate("admin_fights_manage.finished_fights") }}
                </button>
            </div>

            <p>{{ translate("admin_fights_manage.no_fights_in_queue") }}</p>
        </div>
    </div>
</template>

<script setup lang="ts">
    import FightTableRow from "./FightTableRow.vue";
    import {
        eventBus,
        FINISHED_FIGHTS_FOR_MAT_MODAL,
        INSERT_BREAK_BEFORE_FIGHT,
        OPEN_RESHUFFLE_COMPETITORS_MODAL,
        REORDER_FIGHTS_MODAL,
    } from "./figsManageEventBus";
    import DropdownButton from "../Common/DropdownButton.vue";
    import { computed, ref } from "vue";
    import fightsApi from "../Api/fights";
    import { handleError } from "../Common/errorHandler";
    import { FightQueueStatus, isQueueBreak, OrganizerComplexFight } from "../Common/Fights/types";
    import { Mat } from "../Common/types";
    import { useFightSelectionStore } from "./fightsStore";
    import timeFormatter from "../Common/timeFormatter";
    import { translate } from "../Common/translations";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        mat: Mat;
        fightsForMat: OrganizerComplexFight[];
        fightQueueStatus: FightQueueStatus | null;
    }>();
    const emit = defineEmits(["moveFights", "refreshFights"]);

    const fightsStore = useFightSelectionStore();
    const highlightParameterId = ref(0);
    const moveFightsMode = ref(false);

    const totalSelectedFights = computed(() => {
        return Object.keys(fightsStore.selectedFightsToMove).filter(fightId => fightsStore.selectedFightsToMove[fightId] === true).length;
    });

    const totalTimeOfSelectedFights = computed(() => {
        return timeFormatter.formatSecondsToMinutesAndSeconds(
            props.fightsForMat
                .filter(f => fightsStore.selectedFightsToMove[f.id])
                .reduce((sum, f) => sum + (f.fightTime ? f.fightTime.time_in_seconds : 0), 0),
        );
    });

    function fightHover(parameterId: number) {
        highlightParameterId.value = parameterId;
    }

    function startMovingFights() {
        moveFightsMode.value = true;
        fightsStore.matIdToSelectFrom = props.mat.id;
    }

    function openReorderModal() {
        eventBus.$emit(REORDER_FIGHTS_MODAL, { fights: props.fightsForMat, mat: props.mat });
    }

    function openFinishedFights() {
        eventBus.$emit(FINISHED_FIGHTS_FOR_MAT_MODAL, props.mat.id);
    }

    function openReshuffleCompetitorsModal(fightId) {
        eventBus.$emit(OPEN_RESHUFFLE_COMPETITORS_MODAL, fightId);
    }

    function refreshFights(matId) {
        emit("refreshFights", matId);
    }

    function cancelMovingFights() {
        moveFightsMode.value = false;
        fightsStore.selectedFightsToMove = {};
        fightsStore.matIdToSelectFrom = null;
    }

    function confirmSelectedFights() {
        if (Object.keys(fightsStore.selectedFightsToMove).length === 0) {
            alert(translate("admin_fights_manage.select_fights_to_move"));
            return;
        }

        emit("moveFights", props.mat, fightsStore.selectedFightsToMove);

        fightsStore.selectedFightsToMove = {};
        fightsStore.matIdToSelectFrom = null;
        moveFightsMode.value = false;
    }

    function onSelectedCategoryToMove(fight: OrganizerComplexFight) {
        //Find state of selection for fightId
        const isSelected = fightsStore.selectedFightsToMove[fight.id];
        props.fightsForMat.forEach(f => {
            if (f.parameterId === fight.parameterId) {
                // Toggle selection state
                fightsStore.selectedFightsToMove[f.id] = !isSelected;
            }
        });
    }

    function insertBreakBeforeFight(fight) {
        eventBus.$emit(INSERT_BREAK_BEFORE_FIGHT, fight);
    }

    function removeQueueBreak(fight) {
        if (!confirm(translate("common.are_you_sure"))) {
            return Promise.resolve();
        }

        return fightsApi
            .removeQueueBreak(props.eventId, fight.id)
            .then(() => {
                return refreshFights(props.mat.id);
            })
            .catch(handleError);
    }

    function isFightQueueBreak(fight: OrganizerComplexFight) {
        return isQueueBreak(fight);
    }
</script>

<style scoped>
    .bottom-buffer {
        margin-bottom: 5px;
    }

    /* 1. declare transition */
    .fade-move,
    .fade-enter-active,
    .fade-leave-active {
        transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
    }

    /* 2. declare enter from and leave to state */
    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
        transform: scaleY(0.01) translate(30px, 0);
    }

    /* 3. ensure leaving items are taken out of layout flow so that moving
          animations can be calculated correctly. */
    .fade-leave-active {
        position: absolute;
    }
</style>
